<div class="clickup-page">
  <!-- Header -->
  <header class="page-header">
    <nav class="breadcrumb">
      <a routerLink="/home/integrations">Integrations</a>
      <span class="sep">/</span>
      <span>ClickUp</span>
    </nav>
    <div class="header-row">
      <div class="brand">
        <div class="brand-icon">CU</div>
        <div>
          <h1>ClickUp</h1>
          <p>Sync SigmaT WBS activities with ClickUp tasks — bidirectional updates for plan, status, and assignees.</p>
        </div>
      </div>
      <div class="connection-pill" [class.connected]="isConnected" [class.disconnected]="!isConnected">
        <span class="dot"></span>
        {{ connectionLabel }}
      </div>
    </div>
  </header>

  <!-- Status toast -->
  <div class="status-banner" *ngIf="statusMessage" [class]="'tone-' + statusTone">
    <span class="status-text">{{ statusMessage }}</span>
    <button type="button" class="status-dismiss" (click)="statusMessage = ''" aria-label="Dismiss">×</button>
  </div>

  <div class="layout">
    <!-- Step sidebar -->
    <aside class="step-nav">
      <div class="scope-card">
        <label class="field-label">Scope</label>
        <input
          type="number"
          class="field-input"
          [(ngModel)]="projectId"
          placeholder="Org-wide (blank)"
          (change)="loadStatus()"
        />
        <p class="field-hint">Leave blank for org-level config. Required for WBS sync.</p>
        <button type="button" class="btn btn-ghost btn-sm" (click)="loadStatus()" [disabled]="loadingStatus">
          {{ loadingStatus ? 'Refreshing…' : 'Refresh status' }}
        </button>
      </div>

      <nav class="steps">
        <button
          type="button"
          *ngFor="let step of steps; let i = index"
          class="step-item"
          [class.active]="activeStep === step.id"
          [class.done]="(step.id === 'connect' && isConnected) || (step.id === 'configure' && configReady)"
          (click)="setStep(step.id)"
        >
          <span class="step-num">{{ i + 1 }}</span>
          <span class="step-info">
            <strong>{{ step.label }}</strong>
            <small>{{ step.desc }}</small>
          </span>
        </button>
      </nav>

      <div class="quick-stats" *ngIf="listPreview">
        <div class="stat">
          <span class="stat-val">{{ listPreview.task_count }}</span>
          <span class="stat-lbl">Tasks</span>
        </div>
        <div class="stat">
          <span class="stat-val">{{ listPreview.clickup_fields?.length || 0 }}</span>
          <span class="stat-lbl">Fields</span>
        </div>
      </div>
    </aside>

    <!-- Main content -->
    <main class="main-panel">
      <!-- Step 1: Connect -->
      <section class="panel" *ngIf="activeStep === 'connect'">
        <div class="panel-head">
          <h2>Connect ClickUp</h2>
          <p>Authorize SigmaT to access your ClickUp workspace. OAuth is recommended for production.</p>
        </div>

        <div class="auth-method-tabs" *ngIf="oauthAvailable">
          <button type="button" class="method-tab" [class.active]="authMethod === 'oauth'" (click)="authMethod = 'oauth'">
            OAuth 2.0
            <span class="recommended">Recommended</span>
          </button>
          <button type="button" class="method-tab" [class.active]="authMethod === 'token'" (click)="authMethod = 'token'">
            Personal token
          </button>
        </div>

        <div class="panel-body" *ngIf="authMethod === 'oauth' && oauthAvailable">
          <div class="setup-box" *ngIf="oauthSetup?.redirect_uri">
            <h4>Before you connect — register this Redirect URL in ClickUp</h4>
            <p class="setup-warn" *ngIf="oauthSetup?.redirect_uri_warning">
              {{ oauthSetup.redirect_uri_warning }}
            </p>
            <p class="setup-warn">
              “Unable to authorize your teams” almost always means this URL is missing or doesn’t match exactly in your ClickUp OAuth app
              (<a href="https://developer.clickup.com/docs/common_errors" target="_blank" rel="noopener">OAUTH_007</a>).
            </p>
            <div class="redirect-uri-row">
              <code class="redirect-uri">{{ oauthSetup.redirect_uri }}</code>
              <button type="button" class="btn btn-outline btn-sm" (click)="copyRedirectUri()">Copy</button>
            </div>
            <ol class="setup-steps">
              <li>ClickUp → <strong>Settings → Apps</strong> → open <strong>SigmaT Conflux</strong> (or your app)</li>
              <li>Paste the Redirect URL above into <strong>Redirect URL(s)</strong></li>
              <li>Save, wait a minute, then click <strong>Connect with ClickUp</strong> below</li>
            </ol>
          </div>

          <div class="info-card">
            <p>You'll be redirected to ClickUp to select workspace(s). SigmaT stores an encrypted access token — never your ClickUp password.</p>
            <a href="https://developer.clickup.com/docs/authentication" target="_blank" rel="noopener" class="doc-link">
              Authentication docs →
            </a>
          </div>
          <div class="action-row">
            <button
              type="button"
              class="btn btn-primary btn-lg"
              (click)="connectWithOAuth()"
              [disabled]="connectingOAuth || isConnected"
            >
              {{ connectingOAuth ? 'Redirecting to ClickUp…' : isConnected ? 'Already connected' : 'Connect with ClickUp' }}
            </button>
            <button type="button" class="btn btn-outline" (click)="disconnect()" *ngIf="isConnected">Disconnect</button>
          </div>
          <div class="workspace-grid" *ngIf="statusData?.oauth_workspaces?.length">
            <div class="workspace-chip" *ngFor="let ws of statusData.oauth_workspaces">
              <span class="ws-name">{{ ws.name }}</span>
              <code>{{ ws.id }}</code>
            </div>
          </div>
        </div>

        <div class="panel-body" *ngIf="authMethod === 'token' || !oauthAvailable">
          <div class="alert alert-warning" *ngIf="!oauthAvailable">
            OAuth is not configured on the server. Add <code>CLICKUP_OAUTH_*</code> env vars or use a personal token for testing.
          </div>
          <div class="form-field">
            <label class="field-label">Personal API token</label>
            <input type="password" class="field-input" [(ngModel)]="token" placeholder="pk_..." />
            <p class="field-hint">Generate at ClickUp → Settings → Apps → API Token</p>
          </div>
          <div class="action-row">
            <button type="button" class="btn btn-primary" (click)="connectWithToken()" [disabled]="isConnected && !token">
              Connect
            </button>
            <button type="button" class="btn btn-outline" (click)="disconnect()" *ngIf="isConnected">Disconnect</button>
          </div>
        </div>

        <footer class="panel-footer" *ngIf="isConnected">
          <button type="button" class="btn btn-primary" (click)="nextStep()">Continue to configuration →</button>
        </footer>
      </section>

      <!-- Step 2: Configure -->
      <section class="panel" *ngIf="activeStep === 'configure'">
        <div class="panel-head">
          <h2>Configure list & fields</h2>
          <p>Link a ClickUp list and map custom fields for two-way sync.</p>
        </div>

        <div class="panel-body">
          <div class="alert alert-info" *ngIf="!isConnected">
            Connect ClickUp first before loading a list.
          </div>

          <div class="form-section">
            <h3>Load from ClickUp URL</h3>
            <div class="input-group">
              <input
                type="url"
                class="field-input flex-grow"
                [(ngModel)]="clickupUrl"
                placeholder="https://app.clickup.com/{workspace}/v/li/{list_id}"
              />
              <button type="button" class="btn btn-primary" (click)="loadFromUrl()" [disabled]="loadingList || !isConnected">
                {{ loadingList ? 'Loading…' : 'Load list' }}
              </button>
            </div>
          </div>

          <div class="list-preview" *ngIf="listPreview">
            <div class="preview-header">
              <div>
                <h3>{{ listPreview.list?.name || 'ClickUp list' }}</h3>
                <p class="preview-meta">
                  <span *ngIf="listPreview.list?.space">{{ listPreview.list.space }}</span>
                  <span *ngIf="listPreview.list?.folder"> · {{ listPreview.list.folder }}</span>
                </p>
              </div>
              <div class="preview-ids">
                <span>List <code>{{ listPreview.list_id }}</code></span>
              </div>
            </div>

            <div class="task-scroll" *ngIf="listPreview.tasks?.length">
              <table class="data-table">
                <thead>
                  <tr>
                    <th>Task</th>
                    <th>Status</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  <tr *ngFor="let t of listPreview.tasks">
                    <td>
                      <span class="task-name">{{ t.name }}</span>
                      <code class="task-id">{{ t.id }}</code>
                    </td>
                    <td>
                      <select
                        class="status-select"
                        [value]="t.status || ''"
                        [disabled]="updatingTaskId === t.id || !listStatuses.length"
                        (change)="updateTaskStatus(t, $any($event.target).value)"
                        [style.borderColor]="t.status_color || null"
                      >
                        <option value="" disabled *ngIf="!t.status">— select —</option>
                        <option *ngFor="let s of listStatuses" [value]="s.status">
                          {{ s.status }}
                        </option>
                      </select>
                      <span class="status-updating" *ngIf="updatingTaskId === t.id">Saving…</span>
                    </td>
                    <td>
                      <button type="button" class="btn btn-ghost btn-sm" (click)="copyTaskId(t.id)">Copy ID</button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p class="field-hint" *ngIf="listPreview.tasks?.length && listStatuses.length">
              Status changes are written directly to ClickUp (read-only in SigmaT until WBS pull is configured).
            </p>
          </div>

          <div class="form-grid">
            <div class="form-field">
              <label class="field-label">List ID</label>
              <input type="text" class="field-input" [(ngModel)]="listId" placeholder="ClickUp list ID" />
            </div>
            <div class="form-field">
              <label class="field-label">Workspace ID</label>
              <input type="text" class="field-input" [(ngModel)]="workspaceId" placeholder="ClickUp workspace (team) ID" />
            </div>
            <div class="form-field full-width">
              <label class="field-label">SigmaT URL base <span class="optional">optional</span></label>
              <input type="url" class="field-input" [(ngModel)]="sigmatUrlBase" placeholder="https://your-sigmat-domain.com" />
            </div>
          </div>
        </div>

        <footer class="panel-footer">
          <button type="button" class="btn btn-outline" (click)="setStep('connect')">← Back</button>
          <button type="button" class="btn btn-primary" (click)="saveConfig()" [disabled]="savingConfig">
            {{ savingConfig ? 'Saving…' : 'Save configuration' }}
          </button>
          <button type="button" class="btn btn-ghost" (click)="nextStep()" *ngIf="configReady">Continue to sync →</button>
        </footer>
      </section>

      <!-- Step 3: Sync -->
      <section class="panel" *ngIf="activeStep === 'sync'">
        <div class="panel-head">
          <h2>Sync</h2>
          <p>Push SigmaT WBS activities and work orders to ClickUp, or pull task updates back.</p>
        </div>

        <div class="panel-body">
          <div class="sync-cards">
            <article class="sync-card">
              <div class="sync-icon push">↑</div>
              <h3>Push WBS → ClickUp</h3>
              <p>Creates or updates ClickUp tasks for every WBS row in the selected SigmaT project.</p>
              <ul class="checklist">
                <li [class.ok]="isConnected">ClickUp connected</li>
                <li [class.ok]="configReady">List configured</li>
                <li [class.ok]="!!projectId">Project ID set</li>
              </ul>
              <button type="button" class="btn btn-primary btn-block" (click)="pushProjectWbs()" [disabled]="pushing || !canSync">
                {{ pushing ? 'Pushing…' : 'Push all WBS activities' }}
              </button>
              <button
                type="button"
                class="btn btn-outline btn-block"
                (click)="pushWorkOrders()"
                [disabled]="pushingWorkOrders || !canSync"
              >
                {{ pushingWorkOrders ? 'Pushing…' : 'Push work orders' }}
              </button>
              <p class="field-hint">Work orders sync as tasks (number, vendor, notes). ClickUp status changes pull back onto the work order.</p>
            </article>

            <article class="sync-card">
              <div class="sync-icon pull">↓</div>
              <h3>Pull ClickUp → SigmaT</h3>
              <p>Import changes from ClickUp into mapped WBS activities and work orders.</p>
              <button
                type="button"
                class="btn btn-primary btn-block"
                (click)="pullAllTasks()"
                [disabled]="pullingAll || !projectId"
              >
                {{ pullingAll ? 'Pulling all…' : 'Pull all tasks from list' }}
              </button>
              <div class="form-field">
                <label class="field-label">Or a single ClickUp task ID</label>
                <input type="text" class="field-input" [(ngModel)]="pullTaskId" placeholder="e.g. 86d35aytq" />
              </div>
              <button
                type="button"
                class="btn btn-outline btn-block"
                (click)="pullTask()"
                [disabled]="pulling || !projectId || !pullTaskId.trim()"
              >
                {{ pulling ? 'Pulling…' : 'Pull task' }}
              </button>
            </article>
          </div>

          <div class="alert alert-warning" *ngIf="!projectId">
            Set a <strong>Project ID</strong> in the sidebar scope field to enable sync.
          </div>
        </div>

        <footer class="panel-footer">
          <button type="button" class="btn btn-outline" (click)="setStep('configure')">← Back</button>
          <button type="button" class="btn btn-ghost" (click)="setStep('advanced')">Advanced settings →</button>
        </footer>
      </section>

      <!-- Step 4: Advanced -->
      <section class="panel" *ngIf="activeStep === 'advanced'">
        <div class="panel-head">
          <h2>Advanced</h2>
          <p>Field mapping JSON and assignee sync configuration.</p>
        </div>

        <div class="panel-body">
          <div class="form-section">
            <h3>
              Live sync (webhook)
              <span class="connection-pill" [class.connected]="webhookRegistered" [class.disconnected]="!webhookRegistered" style="margin-left: 8px; font-size: 12px;">
                <span class="dot"></span>
                {{ webhookRegistered ? 'Active' : 'Off' }}
              </span>
            </h3>
            <p class="section-desc">
              Registers a ClickUp webhook on the configured list so task changes sync into SigmaT automatically.
              The server must be reachable from the internet.
            </p>
            <div class="form-field" *ngIf="!webhookRegistered">
              <label class="field-label">Webhook endpoint <span class="optional">optional override</span></label>
              <input
                type="url"
                class="field-input"
                [(ngModel)]="webhookEndpoint"
                placeholder="https://your-api-domain.com/api/integrations/clickup/webhook"
              />
              <p class="field-hint">Leave blank to use the server's public URL.</p>
            </div>
            <p class="field-hint" *ngIf="webhookRegistered">
              Webhook <code>{{ webhookInfo.id }}</code> → <code>{{ webhookInfo.endpoint }}</code>
              <span *ngIf="webhookInfo.registered_at"> (registered {{ webhookInfo.registered_at | date: 'medium' }})</span>
            </p>
            <div class="action-row">
              <button
                type="button"
                class="btn btn-primary"
                *ngIf="!webhookRegistered"
                (click)="registerWebhook()"
                [disabled]="registeringWebhook || !isConnected || !configReady"
              >
                {{ registeringWebhook ? 'Enabling…' : 'Enable live sync' }}
              </button>
              <button
                type="button"
                class="btn btn-outline"
                *ngIf="webhookRegistered"
                (click)="unregisterWebhook()"
                [disabled]="unregisteringWebhook"
              >
                {{ unregisteringWebhook ? 'Disabling…' : 'Disable live sync' }}
              </button>
            </div>
          </div>

          <div class="form-section">
            <h3>Assignee mapping</h3>
            <p class="section-desc">Map ClickUp users to SigmaT staff so assignee changes sync on pull.</p>
            <div class="form-grid three-col">
              <div class="form-field">
                <label class="field-label">ClickUp user ID</label>
                <input type="text" class="field-input" [(ngModel)]="mapClickupUserId" />
              </div>
              <div class="form-field">
                <label class="field-label">ClickUp email</label>
                <input type="email" class="field-input" [(ngModel)]="mapClickupEmail" />
              </div>
              <div class="form-field">
                <label class="field-label">SigmaT staff ID</label>
                <input type="number" class="field-input" [(ngModel)]="mapSigmatStaffId" />
              </div>
            </div>
            <button type="button" class="btn btn-outline" (click)="saveUserMap()">Save assignee mapping</button>
          </div>

          <div class="form-section">
            <h3>Custom field IDs</h3>
            <p class="section-desc">Auto-filled when you load a list URL. Create fields named <code>sigmat_*</code> in ClickUp for best results.</p>
            <textarea class="field-textarea code" rows="8" [(ngModel)]="customFieldsJson"></textarea>
          </div>

          <div class="form-section">
            <h3>Field sync directions</h3>
            <p class="section-desc"><code>sigmat_to_clickup</code> or <code>clickup_to_sigmat</code> per field.</p>
            <textarea class="field-textarea code" rows="8" [(ngModel)]="fieldDirectionsJson"></textarea>
          </div>

          <details class="debug-details">
            <summary>Connection debug JSON</summary>
            <pre class="debug-pre" *ngIf="statusData">{{ statusData | json }}</pre>
          </details>
        </div>

        <footer class="panel-footer">
          <button type="button" class="btn btn-outline" (click)="setStep('sync')">← Back</button>
          <button type="button" class="btn btn-primary" (click)="saveConfig()" [disabled]="savingConfig">
            {{ savingConfig ? 'Saving…' : 'Save all settings' }}
          </button>
        </footer>
      </section>
    </main>
  </div>
</div>
